import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FinishCycleRequest, PauseCycleRequest, StartCycleRequest } from '../../interfaces/attendance-cycle.dto';
import { PasswordDTO } from '../../interfaces/password-dto';
import { PasswordService } from '../password/password.service';

const CYCLE_KEY = 'attendanceCycleId';

@Injectable({ providedIn: 'root' })
export class AttendanceCycleService {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/attendance-cycle`;

  private _cycleId: number | null = null;
  private _pauseStartedAt: number | null = null;
  private _totalPausedSeconds = 0;

  get cycleId(): number | null { return this._cycleId; }

  constructor(private http: HttpClient, private passwordService: PasswordService) {
    const stored = sessionStorage.getItem(CYCLE_KEY);
    if (stored) this._cycleId = Number(stored);
  }

  /** Retomada proativa: senha ja em atendimento aberto pra este operador (F5/retorno). */
  async getOpenPassword(clinicId: number, username: string): Promise<PasswordDTO | undefined> {
    try {
      const response = await firstValueFrom(this.http.get<any>(
        `${this.apiURL}/open-password?clinicId=${clinicId}&username=${encodeURIComponent(username)}`,
      ));
      return this.passwordService.mapearDoCustomReturn(response);
    } catch { return undefined; }
  }

  /** Media do dia (DT_CHAMADA -> DT_FIM) do proprio operador; 0 quando ainda nao fechou atendimento. */
  async getAvgAttendanceSeconds(clinicId: number, username: string): Promise<number> {
    try {
      return await firstValueFrom(this.http.get<number>(
        `${this.apiURL}/avg-attendance?clinicId=${clinicId}&username=${encodeURIComponent(username)}`,
      ));
    } catch { return 0; }
  }

  async startCycle(req: StartCycleRequest): Promise<number | null> {
    try {
      const response = await firstValueFrom(this.http.post<any>(`${this.apiURL}/start`, req));
      const id = response?.data?.[0];
      if (id) {
        this._cycleId = id;
        this._totalPausedSeconds = 0;
        this._pauseStartedAt = null;
        sessionStorage.setItem(CYCLE_KEY, String(id));
      }
      return id;
    } catch {
      return null;
    }
  }

  async pause(pauseReason: string): Promise<void> {
    if (!this._cycleId) return;
    this._pauseStartedAt = Date.now();
    const req: PauseCycleRequest = { pauseReason, isPausing: true, pausedSeconds: 0 };
    try {
      await firstValueFrom(this.http.put(`${this.apiURL}/${this._cycleId}/pause`, req));
    } catch { /* ignora falha — estado local já foi atualizado */ }
  }

  async resume(): Promise<void> {
    if (!this._cycleId) return;
    const pausedSec = this._pauseStartedAt
      ? Math.floor((Date.now() - this._pauseStartedAt) / 1000)
      : 0;
    this._totalPausedSeconds += pausedSec;
    this._pauseStartedAt = null;
    const req: PauseCycleRequest = { pauseReason: '', isPausing: false, pausedSeconds: pausedSec };
    try {
      await firstValueFrom(this.http.put(`${this.apiURL}/${this._cycleId}/pause`, req));
    } catch { /* ignora */ }
  }

  async markCalled(passwordId: number): Promise<void> {
    if (!this._cycleId) return;
    try {
      await firstValueFrom(this.http.put(`${this.apiURL}/${this._cycleId}/called`, passwordId));
    } catch { /* ignora */ }
  }

  async finish(result: 'ATENDIDO' | 'NAO_ATENDIDO' | 'DESCARTADO' | 'SEM_PACIENTE'): Promise<void> {
    if (!this._cycleId) return;
    const req: FinishCycleRequest = { result };
    try {
      await firstValueFrom(this.http.put(`${this.apiURL}/${this._cycleId}/finish`, req));
    } catch { /* ignora */ }
    this._cycleId = null;
    sessionStorage.removeItem(CYCLE_KEY);
  }

  get isPaused(): boolean { return this._pauseStartedAt !== null; }
  get totalPausedSeconds(): number { return this._totalPausedSeconds; }
}
